import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Button,
  Input,
  Space,
  Modal,
  Form,
  message,
  Tag,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  Drawer,
  Descriptions,
  Divider,
  Table,
  Alert,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  GlobalOutlined,
  MobileOutlined,
  DesktopOutlined,
  SafetyOutlined,
  LinkOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  FacebookFilled,
  SendOutlined,
  CopyOutlined,
  RobotOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { visitorService } from '../services/visitor';
import { blacklistService, BlacklistType } from '../services/blacklist';
import { useDeviceConstants } from '../hooks/useDeviceConstants';
import { useLocation, useNavigate } from 'react-router-dom';
// 简化：内置表格已足够，移除外部组件
import { 
  FormattedVisitorRecord, 
  formatVisitorRecords,
  getNetworkFeatureTags,
  getLocationText
} from '../utils/visitorDataFormatter';
import PostbackModal from '../components/PostbackModal';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;

const VisitorManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fromLinkManagement = location.state?.fromLinkManagement;
  const linkIdFromState = location.state?.linkId;
  // 从 URL 参数获取 link_id（用于来自链接管理页面的跳转）
  const linkIdFromUrl = new URLSearchParams(location.search).get('link_id');
  const linkId = linkIdFromState || (linkIdFromUrl ? parseInt(linkIdFromUrl) : undefined);
  const linkTitle = location.state?.linkTitle;
  const { constants, loading: constantsLoading, error: constantsError } = useDeviceConstants();

  const buildOptions = useCallback((record?: Record<string, string>) => {
    if (!record) return [] as Array<{ value: string; label: string }>;
    return Object.entries(record)
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'zh-CN', { sensitivity: 'base' }));
  }, []);

  const countryOptions = useMemo(() => buildOptions(constants?.country_codes), [buildOptions, constants]);
  const deviceTypeOptions = useMemo(() => buildOptions(constants?.device_types), [buildOptions, constants]);
  const osTypeOptions = useMemo(() => buildOptions(constants?.os_types), [buildOptions, constants]);
  const clientTypeOptions = useMemo(() => buildOptions(constants?.client_types), [buildOptions, constants]);
  const networkTypeOptions = useMemo(() => buildOptions(constants?.network_types), [buildOptions, constants]);

  const accessStatusOptions = useMemo(
    () => [
      { value: 'normal', label: '正常访问' },
      { value: 'intercepted', label: '拦截访问' },
    ],
    []
  );

  const vpnProxyOptions = useMemo(
    () => [
      { value: 'has_proxy', label: '命中代理/VPN' },
      { value: 'no_proxy', label: '无代理/VPN' },
    ],
    []
  );

  const booleanFilterOptions = useMemo(
    () => [
      { value: 'yes', label: '是' },
      { value: 'no', label: '否' },
    ],
    []
  );

  const isOptionsLoading = constantsLoading && !constants;

  type FiltersState = {
    dateRange: [Dayjs, Dayjs];
    websiteGroup: string;
    shortCode: string;
    country: string;
    accessStatus: string;
    vpnProxy: string;
    datacenter: string;
    satellite: string;
    ipLocation: string;
    networkType: string;
    deviceType: string;
    deviceSystem: string;
    clientType: string;
    clientLanguage: string;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmTerm: string;
    utmContent: string;
    link_id?: number;
    client_id?: number;
    requestUrl: string;
    visitorIp: string;
  };

  const getDefaultDateRange = (): [Dayjs, Dayjs] => [dayjs().subtract(3, 'day'), dayjs()];

  const createDefaultFilters = (): FiltersState => ({
    dateRange: getDefaultDateRange(),
    websiteGroup: '',
    shortCode: '',
    country: '',
    accessStatus: '',
    vpnProxy: '',
    datacenter: '',
    satellite: '',
    ipLocation: '',
    networkType: '',
    deviceType: '',
    deviceSystem: '',
    clientType: '',
    clientLanguage: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
    link_id: linkId,
    client_id: undefined,
    requestUrl: '',
    visitorIp: '',
  });

  const [formattedVisitors, setFormattedVisitors] = useState<FormattedVisitorRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<FormattedVisitorRecord | null>(null);
  const [fbEventModalVisible, setFbEventModalVisible] = useState(false);
  const [fbSubmitLoading, setFbSubmitLoading] = useState(false);
  const [fbEventForm] = Form.useForm();
  const rawSelectedRecord = selectedRecord?.raw;

  // 黑名单相关状态
  const [blacklistModalVisible, setBlacklistModalVisible] = useState(false);
  const [blacklistRecord, setBlacklistRecord] = useState<FormattedVisitorRecord | null>(null);
  const [blacklistForm] = Form.useForm();

  // 像素回传相关状态
  const [postbackModalVisible, setPostbackModalVisible] = useState(false);
  const [postbackVisitorId, setPostbackVisitorId] = useState<number>(0);

  const facebookEventOptions = [
    { value: 'AddPaymentInfo', label: '添加支付信息 (AddPaymentInfo)' },
    { value: 'AddToCart', label: '加入购物车 (AddToCart)' },
    { value: 'AddToWishlist', label: '加入心愿单 (AddToWishlist)' },
    { value: 'CompleteRegistration', label: '完成注册 (CompleteRegistration)' },
    { value: 'Contact', label: '留下联系方式 (Contact)' },
    { value: 'CustomizeProduct', label: '定制商品 (CustomizeProduct)' },
    { value: 'Donate', label: '捐款 (Donate)' },
    { value: 'FindLocation', label: '查找网点 (FindLocation)' },
    { value: 'InitiateCheckout', label: '发起结账 (InitiateCheckout)' },
    { value: 'Lead', label: '留下潜在客户信息 (Lead)' },
    { value: 'Purchase', label: '购物 (Purchase)' },
    { value: 'Schedule', label: '排期 (Schedule)' },
    { value: 'Search', label: '搜索 (Search)' },
    { value: 'StartTrial', label: '开始试用 (StartTrial)' },
    { value: 'SubmitApplication', label: '提交申请 (SubmitApplication)' },
    { value: 'Subscribe', label: '订阅 (Subscribe)' },
    { value: 'ViewContent', label: '查看内容 (ViewContent)' },
  ];
  const [filters, setFilters] = useState<FiltersState>(() => createDefaultFilters());
  const [tempFilters, setTempFilters] = useState<FiltersState>(() => createDefaultFilters());
  const [shortCodeInput, setShortCodeInput] = useState<string>('');
  const [clientIdInput, setClientIdInput] = useState<string>('');
  
  // 用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);
  // 用于防抖的定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (constantsError) {
      message.error('加载筛选常量失败，部分筛选项可能不可用');
    }
  }, [constantsError]);

  const loadVisitors = useCallback(
    async () => {
      console.log('loadVisitors called at:', new Date().toISOString());
      console.log('🔍 当前筛选条件:', filters);
      console.log('🔗 链接ID:', linkId);
      console.log('📋 来自链接管理:', fromLinkManagement);

      const targetPage = currentPage;
      const targetSize = pageSize;

      // 清除之前的防抖定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // 取消之前的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // 创建新的 AbortController
      abortControllerRef.current = new AbortController();
      const currentSignal = abortControllerRef.current.signal;

      // 设置防抖延迟
      debounceTimerRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          // 转换 dateRange 为日期字符串格式
          const [startDate, endDate] = filters.dateRange;
          const requestParams: Record<string, unknown> = {
            ...filters,
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            page: targetPage,
            size: targetSize,
            limit: targetSize,
          };
          delete (requestParams as any).dateRange; // 删除 dateRange 字段
          delete (requestParams as any).websiteGroup; // 删除 websiteGroup 字段（前端不使用）

          const response = await visitorService.getVisitorRecords(requestParams);
          // 检查请求是否被取消
          if (!currentSignal.aborted) {
            // 处理 null 或 undefined 的情况
            const listData = response.list || [];
            // 格式化数据用于新表格组件
            const formatted = formatVisitorRecords(listData as any[]) as any;
            setFormattedVisitors(formatted);

            const responsePage = response.page ?? targetPage;
            const responseSize = response.size ?? targetSize;
            const resolvedTotal = typeof response.total === 'number' ? response.total : listData.length;

            setTotal(resolvedTotal);
            setCurrentPage((prev) => (prev === responsePage ? prev : responsePage));
            setPageSize((prev) => (prev === responseSize ? prev : responseSize));
          }
        } catch (error: any) {
          // 忽略被取消的请求错误
          if (error.name !== 'AbortError') {
            message.error('加载访客记录失败');
          }
        } finally {
          if (!currentSignal.aborted) {
            setLoading(false);
          }
        }
      }, 100); // 100ms 防抖延迟
    },
    [filters, currentPage, pageSize]
  );

  useEffect(() => {
    loadVisitors();
    
    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadVisitors]);

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setCurrentPage(1);
  };
  
  const handleResetFilters = () => {
    const defaultFilters = createDefaultFilters();
    const newFilters = fromLinkManagement ? defaultFilters : { ...defaultFilters, link_id: undefined };
    setTempFilters(newFilters);
    setFilters(newFilters);
    setShortCodeInput('');
    setClientIdInput('');
    setCurrentPage(1);
  };

  const handleViewDetail = (record: FormattedVisitorRecord) => {
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  const handleCopyText = async (text: string, successMessage: string) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        message.success(successMessage);
        return;
      }
    } catch (error) {
      console.warn('[VisitorManagement] clipboard API failed, fallback to execCommand', error);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      message.success(successMessage);
    } catch (error) {
      console.error('[VisitorManagement] fallback copy failed', error);
      message.warning('无法复制到剪贴板，请手动复制');
    }
  };

  const extractFbclidFromUrl = (input?: string) => {
    if (!input) return '';
    try {
      const hasProtocol = /^https?:\/\//i.test(input);
      const fallbackBase = 'https://placeholder.local';
      const normalized = hasProtocol ? input : `${fallbackBase}${input.startsWith('/') ? '' : '/'}${input}`;
      const value = new URL(normalized).searchParams.get('fbclid');
      if (value) return value;
    } catch {
      const match = input.match(/[?&]fbclid=([^&#]*)/i);
      if (match && match[1]) {
        try {
          return decodeURIComponent(match[1]);
        } catch {
          return match[1];
        }
      }
    }
    return '';
  };

  const getRecordFbclid = (record?: FormattedVisitorRecord | null) => {
    if (!record) return '';
    const candidates = [
      record.request_url,
      record.redirect_url,
      record.raw?.request_url,
      record.raw?.redirect_url,
    ];
    for (const candidate of candidates) {
      const value = extractFbclidFromUrl(candidate);
      if (value) return value;
    }
    return '';
  };

  /**
   * 渲染网络安全检测结果标签
   * @param flag 是否命中检测项
   * @param positiveLabel 命中时展示的文本
   * @param negativeLabel 未命中时展示的文本
   * @returns 对应的标签元素
   */
  const renderSecurityTag = (
    flag?: boolean,
    positiveLabel: string = '已检测到风险',
    negativeLabel: string = '未检测到风险'
  ) => {
    if (flag === undefined || flag === null) {
      return <Tag color="default">未知</Tag>;
    }
    return (
      <Tag color={flag ? 'red' : 'green'}>
        {flag ? positiveLabel : negativeLabel}
      </Tag>
    );
  };

  const handleOpenFacebookModal = (record: FormattedVisitorRecord) => {
    const fbclid = getRecordFbclid(record);
    if (!fbclid) {
      message.warning('当前记录缺少 fbclid，无法生成回传事件');
      return;
    }

    setSelectedRecord(record);
    fbEventForm.setFieldsValue({
      event_name: 'Contact',
      action_source: 'chat',
      event_time: dayjs(),
      custom_data_value: undefined,
      currency: 'USD',
      notes: '',
    });
    setFbEventModalVisible(true);
  };

  const getBotTagColor = (botType: string) => {
    switch ((botType || '').toLowerCase()) {
      case 'search_engine':
        return 'geekblue';
      case 'social_media':
        return 'purple';
      case 'monitoring':
        return 'cyan';
      case 'seo_tool':
        return 'orange';
      case 'generic':
        return 'magenta';
      default:
        return 'default';
    }
  };

  const renderBotTag = (botInfo?: FormattedVisitorRecord['bot_info']) => {
    if (!botInfo || !botInfo.is_bot) {
      return (
        <Tag icon={<CheckCircleOutlined />} color="default">
          人类访客
        </Tag>
      );
    }
    const color = getBotTagColor(botInfo.bot_type || '');
    const label = botInfo.bot_type || '未知爬虫';
    return (
      <Tag icon={<RobotOutlined />} color={color}>
        {label}
      </Tag>
    );
  };

  const formatCoordinate = (lat?: number, lng?: number) => {
    if (lat === undefined || lat === null || lng === undefined || lng === null) {
      return '未提供';
    }
    return `${lat}, ${lng}`;
  };

  const formatProcessingTime = (value?: number | null) => {
    if (value === undefined || value === null) {
      return '未提供';
    }
    return `${value} ms`;
  };

  const handleSubmitFacebookEvent = async (values: any) => {
    const record = selectedRecord;
    if (!record) {
      message.error('请先选择一条访客记录');
      return;
    }

    const fbclid = getRecordFbclid(record);
    if (!fbclid) {
      message.error('缺少 fbclid，无法构建回传参数');
      return;
    }

    setFbSubmitLoading(true);
    try {
      const eventTime = values.event_time ? values.event_time.unix() : Math.floor(Date.now() / 1000);
      const payload = {
        fbclid,
        event_name: values.event_name,
        event_time: eventTime,
        action_source: values.action_source,
        value: values.custom_data_value,
        currency: values.currency || 'USD',
        notes: values.notes,
        visitor_id: record.id,
        link_id: record.link_id,
      };

      console.info('[VisitorManagement] Facebook manual payload', payload);
      message.success('已生成 Facebook 事件回传草稿，稍后可接入 Conversion API');
      setFbEventModalVisible(false);
    } finally {
      setFbSubmitLoading(false);
    }
  };

  const handleExport = () => {
    message.info('导出功能开发中');
  };

  // 打开黑名单弹窗
  const handleOpenBlacklistModal = (record: FormattedVisitorRecord) => {
    setBlacklistRecord(record);
    blacklistForm.setFieldsValue({
      blacklist_type: 'ip',
      identifier: record.ip_info.ip,
      remark: '',
    });
    setBlacklistModalVisible(true);
  };

  // 提交黑名单
  const handleSubmitBlacklist = async (values: { blacklist_type: BlacklistType; identifier: string; remark?: string }) => {
    try {
      const response = await blacklistService.addEntry(values);
      if (response.success) {
        message.success('已加入黑名单');
        setBlacklistModalVisible(false);
        blacklistForm.resetFields();
      } else {
        message.error(response.message || '添加失败');
      }
    } catch (error) {
      message.error('添加黑名单失败');
    }
  };

  // 顶部滚动条同步 - 查找表格滚动容器
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const [tableScrollWidth, setTableScrollWidth] = React.useState(1800);

  const getVisitorTableScrollContainer = React.useCallback(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return null;
    return (wrapper.querySelector('.ant-table-body') || wrapper.querySelector('.ant-table-content')) as HTMLDivElement | null;
  }, []);

  React.useEffect(() => {
    const tableContainer = getVisitorTableScrollContainer();
    if (tableContainer) {
      const updateWidth = () => {
        const scrollWidth = tableContainer.scrollWidth;
        if (scrollWidth > 0) {
          setTableScrollWidth(scrollWidth);
        }
      };
      updateWidth();
      const observer = new ResizeObserver(updateWidth);
      observer.observe(tableContainer);
      return () => observer.disconnect();
    }
  }, [formattedVisitors, getVisitorTableScrollContainer]);

  const handleTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const tableContainer = getVisitorTableScrollContainer();
    if (tableContainer) {
      tableContainer.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleTableScroll = React.useCallback(() => {
    const tableContainer = getVisitorTableScrollContainer();
    if (tableContainer && topScrollRef.current) {
      topScrollRef.current.scrollLeft = tableContainer.scrollLeft;
    }
  }, [getVisitorTableScrollContainer]);

  React.useEffect(() => {
    const tableContainer = getVisitorTableScrollContainer();
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleTableScroll);
      return () => tableContainer.removeEventListener('scroll', handleTableScroll);
    }
  }, [formattedVisitors, handleTableScroll, getVisitorTableScrollContainer]);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {fromLinkManagement && (
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/links')}
                style={{ marginRight: 16 }}
              >
                返回链接管理
              </Button>
            )}
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <EyeOutlined style={{ marginRight: 8 }} />
                {fromLinkManagement && linkTitle ? `${linkTitle} - 访客记录` : '访客记录管理'}
              </Title>
              {fromLinkManagement && linkTitle && (
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  链接ID: {linkId}
                </Text>
              )}
            </div>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadVisitors}>
              刷新
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出数据
            </Button>
          </Space>
        </div>
      </Card>

      {/* 高级筛选区域 */}
      <Card size="small" style={{ marginBottom: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0 }}>
              <FilterOutlined style={{ marginRight: 8 }} />
              高级筛选
            </Title>
            <Space>
              <Button type="primary" size="small" onClick={handleApplyFilters}>
                应用筛选
              </Button>
              <Button size="small" onClick={handleResetFilters}>
                重置筛选
              </Button>
            </Space>
          </div>

          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Form.Item label="记录日期">
                <RangePicker
                  value={tempFilters.dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setTempFilters((prev) => ({
                        ...prev,
                        dateRange: [dates[0], dates[1]] as [Dayjs, Dayjs],
                      }));
                    } else {
                      setTempFilters((prev) => ({
                        ...prev,
                        dateRange: getDefaultDateRange(),
                      }));
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {/* <Col span={6}>
              <Form.Item label="网站分组">
                <Select
                  value={filters.websiteGroup}
                  onChange={(value) => setFilters({ ...filters, websiteGroup: value })}
                  placeholder="选择网站分组"
                  allowClear
                >
                  <Option value="product">产品相关</Option>
                  <Option value="docs">文档相关</Option>
                  <Option value="event">活动相关</Option>
                </Select>
              </Form.Item>
            </Col> */}
            <Col span={6}>
              <Form.Item label="短链码">
                <Input
                  value={shortCodeInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    setShortCodeInput(value);
                    setTempFilters((prev) => ({ ...prev, shortCode: value }));
                  }}
                  placeholder="输入短链码"
                  allowClear
                  prefix={<LinkOutlined />}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="客户ID">
                <Input
                  value={clientIdInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    setClientIdInput(value);
                    const clientId = parseInt(value);
                    if (!isNaN(clientId) && clientId > 0) {
                      setTempFilters((prev) => ({ ...prev, client_id: clientId }));
                    } else if (value === '') {
                      setTempFilters((prev) => ({ ...prev, client_id: undefined }));
                    }
                  }}
                  placeholder="输入客户ID"
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="广告投放链接">
                <Input
                  value={tempFilters.requestUrl}
                  onChange={(event) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      requestUrl: event.target.value,
                    }))
                  }
                  placeholder="输入广告投放链接关键词"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="访客 IP">
                <Input
                  value={tempFilters.visitorIp}
                  onChange={(event) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      visitorIp: event.target.value,
                    }))
                  }
                  placeholder="输入访客 IP"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="国家/地区">
                <Select
                  value={tempFilters.country}
                  onChange={(value) => setTempFilters({ ...tempFilters, country: value || '' })}
                  placeholder="选择国家/地区"
                  allowClear
                  showSearch
                  options={countryOptions}
                  optionFilterProp="label"
                  loading={isOptionsLoading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="访问状态">
                <Select
                  value={tempFilters.accessStatus}
                  onChange={(value) => setTempFilters({ ...tempFilters, accessStatus: value })}
                  placeholder="选择访问状态"
                  allowClear
                >
                  <Option value="normal">正常</Option>
                  <Option value="intercepted">拦截</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="VPN网络代理">
                <Select
                  value={tempFilters.vpnProxy}
                  onChange={(value) => setTempFilters({ ...tempFilters, vpnProxy: value })}
                  placeholder="选择VPN代理状态"
                  allowClear
                >
                  <Option value="has_proxy">有代理</Option>
                  <Option value="no_proxy">无代理</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="数据中心机房">
                <Select
                  value={tempFilters.datacenter}
                  onChange={(value) => setTempFilters({ ...tempFilters, datacenter: value })}
                  placeholder="选择数据中心状态"
                  allowClear
                >
                  <Option value="is_datacenter">是</Option>
                  <Option value="not_datacenter">否</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="设备类型">
                <Select
                  value={tempFilters.deviceType}
                  onChange={(value) => setTempFilters({ ...tempFilters, deviceType: value || '' })}
                  placeholder="选择设备类型"
                  allowClear
                  showSearch
                  options={deviceTypeOptions}
                  optionFilterProp="label"
                  loading={isOptionsLoading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="操作系统">
                <Select
                  value={tempFilters.deviceSystem}
                  onChange={(value) => setTempFilters({ ...tempFilters, deviceSystem: value || '' })}
                  placeholder="选择操作系统"
                  allowClear
                  showSearch
                  options={osTypeOptions}
                  optionFilterProp="label"
                  loading={isOptionsLoading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="客户端类型">
                <Select
                  value={tempFilters.clientType}
                  onChange={(value) => setTempFilters({ ...tempFilters, clientType: value || '' })}
                  placeholder="选择客户端类型"
                  allowClear
                  showSearch
                  options={clientTypeOptions}
                  optionFilterProp="label"
                  loading={isOptionsLoading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

        </Card>

        {/* 访客记录表格 */}
        <Card style={{ marginTop: 16 }}>
          {/* 顶部滚动条 */}
          <div 
            ref={topScrollRef}
            onScroll={handleTopScroll}
            style={{ 
              overflowX: 'auto', 
              overflowY: 'hidden',
              height: 16,
              marginBottom: 8,
              background: '#fafafa',
              borderRadius: 4
            }}
          >
            <div style={{ width: tableScrollWidth, height: 1 }} />
          </div>
          <div ref={tableWrapperRef}>
            <Table
            dataSource={formattedVisitors}
            loading={loading}
            rowKey="id"
            size="small"
            scroll={{ x: 1800 }}
            sticky
            columns={[
              {
                title: '访问时间',
                dataIndex: 'ip_info',
                key: 'accessed_at',
                width: 180,
                render: (info) => info.accessed_at,
              },
              {
                title: '访问状态',
                dataIndex: 'access_status',
                key: 'access_status',
                width: 100,
                render: (status) => (
                  <div>
                    <Tag color={status.is_intercepted ? 'red' : 'green'}>
                      {status.is_intercepted ? '拦截' : '正常'}
                    </Tag>
                    {status.is_intercepted && status.intercept_reason && (
                      <div style={{ marginTop: 4, fontSize: '12px', color: '#999' }}>
                        {status.intercept_reason}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                title: '链接编号',
                dataIndex: 'link_code',
                key: 'link_code',
                width: 100,
              },
              {
                title: '客户ID',
                dataIndex: 'client_id',
                key: 'client_id',
                width: 80,
              },
              {
                title: '广告平台',
                dataIndex: 'ad_plat',
                key: 'ad_plat',
                width: 100,
                render: (adPlat: string | undefined) => {
                  const platformConfig: Record<string, { label: string; color: string }> = {
                    facebook: { label: 'Facebook', color: 'blue' },
                    google: { label: 'Google/YouTube', color: 'green' },
                    tiktok: { label: 'TikTok', color: 'magenta' },
                    twitter: { label: 'Twitter/X', color: 'cyan' },
                    microsoft: { label: 'Microsoft/Bing', color: 'orange' },
                    linkedin: { label: 'LinkedIn', color: 'geekblue' },
                    pinterest: { label: 'Pinterest', color: 'red' },
                    snapchat: { label: 'Snapchat', color: 'gold' },
                    kwai: { label: '快手', color: 'volcano' },
                    yahoo: { label: 'Yahoo', color: 'purple' },
                    yandex: { label: 'Yandex', color: 'lime' },
                    seznam: { label: 'Seznam', color: 'cyan' },
                    awin: { label: 'Awin', color: 'geekblue' },
                  };
                  if (!adPlat) return <Tag color="default">-</Tag>;
                  const config = platformConfig[adPlat] || { label: adPlat, color: 'default' };
                  return <Tag color={config.color}>{config.label}</Tag>;
                },
              },
              {
                title: '规则ID',
                dataIndex: 'rule_id',
                key: 'rule_id',
                width: 80,
                render: (ruleId: number | undefined) => {
                  if (!ruleId) return <Tag color="default">-</Tag>;
                  return <Tag color="blue">{ruleId}</Tag>;
                },
              },
              {
                title: '访问信息',
                key: 'url_info',
                width: 300,
                render: (_: unknown, record: FormattedVisitorRecord) => {
                  const renderUrlRow = (label: string, url: string | undefined, color: string) => {
                    if (!url) {
                      return (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 6,
                          padding: '2px 0'
                        }}>
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#8c8c8c',
                            minWidth: '64px',
                            fontWeight: 500
                          }}>
                            {label}
                          </span>
                          <span style={{ color: '#d9d9d9', fontSize: '12px' }}>-</span>
                        </div>
                      );
                    }
                    
                    return (
                      <Tooltip title={url}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 6,
                          padding: '2px 0',
                          maxWidth: '280px'
                        }}>
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#8c8c8c',
                            minWidth: '64px',
                            fontWeight: 500,
                            flexShrink: 0
                          }}>
                            {label}
                          </span>
                          <LinkOutlined style={{ 
                            flex: '0 0 auto', 
                            color: color,
                            fontSize: '12px'
                          }} />
                          <span style={{
                            flex: '1 1 auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '12px',
                            color: '#262626',
                            minWidth: 0
                          }}>
                            {url}
                          </span>
                        </div>
                      </Tooltip>
                    );
                  };

                  return (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 4
                    }}>
                      {renderUrlRow('广告投放链接', record.request_url, '#1890ff')}
                      {renderUrlRow('最终跳转链接', record.access_url, '#52c41a')}
                      {renderUrlRow('来源页面', record.referer, '#fa8c16')}
                    </div>
                  );
                },
              },
              {
                title: '请求参数',
                key: 'request_params',
                width: 220,
                render: (_: unknown, record: FormattedVisitorRecord) => {
                  // 广告平台参数列表（优先展示）
                  const adParamKeys = [
                    'fbclid', 'gclid', 'gclsrc', 'wbraid', 'gbraid', 'dclid',
                    'ttclid', 'msclkid', 'li_fat_id', 'twclid', 'pinclid',
                    'sck', 'yjclid', 'yclid', 'sznclid', 'zanpid',
                    'ksgwid', 'kwai_click_id'
                  ];

                  // 解析 URL 参数
                  const parseParams = (url: string | undefined): Record<string, string> => {
                    if (!url) return {};
                    try {
                      const urlObj = new URL(url);
                      const params: Record<string, string> = {};
                      urlObj.searchParams.forEach((value, key) => {
                        params[key] = value;
                      });
                      return params;
                    } catch {
                      return {};
                    }
                  };

                  const params = parseParams(record.request_url);
                  const allEntries = Object.entries(params);
                  
                  if (allEntries.length === 0) {
                    return <span style={{ color: '#d9d9d9' }}>-</span>;
                  }

                  // 分离广告参数和其他参数，广告参数优先
                  const adEntries = allEntries.filter(([key]) => adParamKeys.includes(key.toLowerCase()));
                  const otherEntries = allEntries.filter(([key]) => !adParamKeys.includes(key.toLowerCase()));
                  const sortedEntries = [...adEntries, ...otherEntries];

                  return (
                    <Tooltip 
                      title={
                        <div style={{ maxWidth: 350 }}>
                          {adEntries.length > 0 && (
                            <>
                              <div style={{ color: '#faad14', fontWeight: 'bold', marginBottom: 4 }}>广告参数:</div>
                              {adEntries.map(([key, value]) => (
                                <div key={key} style={{ marginBottom: 4 }}>
                                  <Text strong style={{ color: '#faad14' }}>{key}:</Text>{' '}
                                  <Text style={{ color: '#fff', wordBreak: 'break-all' }}>{value}</Text>
                                </div>
                              ))}
                            </>
                          )}
                          {otherEntries.length > 0 && (
                            <>
                              {adEntries.length > 0 && <div style={{ borderTop: '1px solid #444', margin: '8px 0' }} />}
                              <div style={{ color: '#8c8c8c', fontWeight: 'bold', marginBottom: 4 }}>其他参数:</div>
                              {otherEntries.map(([key, value]) => (
                                <div key={key} style={{ marginBottom: 4 }}>
                                  <Text strong style={{ color: '#fff' }}>{key}:</Text>{' '}
                                  <Text style={{ color: '#fff', wordBreak: 'break-all' }}>{value}</Text>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      }
                    >
                      <div style={{ 
                        maxWidth: 200,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}>
                        {sortedEntries.slice(0, 3).map(([key, value]) => {
                          const isAdParam = adParamKeys.includes(key.toLowerCase());
                          return (
                            <div key={key} style={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              <span style={{ color: isAdParam ? '#faad14' : '#1890ff', fontWeight: isAdParam ? 'bold' : 'normal' }}>
                                {key}
                              </span>
                              <span style={{ color: '#8c8c8c' }}>: </span>
                              <span>{value.length > 12 ? value.slice(0, 12) + '...' : value}</span>
                            </div>
                          );
                        })}
                        {sortedEntries.length > 3 && (
                          <span style={{ color: '#8c8c8c' }}>+{sortedEntries.length - 3} 更多...</span>
                        )}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '爬虫识别',
                key: 'bot_info',
                width: 130,
                render: (_: unknown, record: FormattedVisitorRecord) => renderBotTag(record.bot_info),
              },
              {
                title: 'IP地址',
                dataIndex: 'ip_info',
                key: 'ip',
                width: 140,
                render: (info) => info.ip,
              },
              {
                title: '地理位置',
                dataIndex: 'ip_info',
                key: 'location',
                width: 200,
                render: (info) => {
                  const locationText = getLocationText(info);
                  return locationText && locationText.trim() !== '' ? locationText : '-';
                },
              },
              {
                title: '访客设备信息',
                key: 'device_info',
                width: 240,
                render: (_: unknown, record: FormattedVisitorRecord) => {
                  const renderInfoRow = (label: string, content: string | React.ReactNode, icon?: React.ReactNode) => {
                    return (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6,
                        padding: '2px 0',
                        lineHeight: '1.4'
                      }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#8c8c8c',
                          minWidth: '56px',
                          fontWeight: 500
                        }}>
                          {label}：
                        </span>
                        {icon && <span style={{ fontSize: '12px' }}>{icon}</span>}
                        <span style={{
                          flex: '1 1 auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '12px',
                          color: '#262626'
                        }}>
                          {content}
                        </span>
                      </div>
                    );
                  };

                  const device = record.device_system;
                  const client = record.client_info;
                  
                  // 设备类型标签
                  const deviceTypeTag = (
                    <Tag 
                      icon={<MobileOutlined />}
                      color={
                        device.device_type === 'mobile' ? 'blue' :
                        device.device_type === 'tablet' ? 'green' :
                        device.device_type === 'desktop' ? 'orange' : 'default'
                      }
                      style={{ fontSize: '11px', padding: '0 4px' }}
                    >
                      {device.device_type === 'mobile' ? '移动' :
                       device.device_type === 'tablet' ? '平板' :
                       device.device_type === 'desktop' ? '桌面' : device.device_type}
                    </Tag>
                  );

                  // 品牌型号
                  const brandModel = `${device.device_brand || '未知'} ${device.device_model || ''}`.trim();
                  
                  // 操作系统
                  const osInfo = `${device.os_type} ${device.os_version}`.trim();
                  
                  // 浏览器
                  const browserInfo = `${client.browser_type || ''} ${client.browser_version || ''}`.trim() || '-';
                  
                  // 客户端类型
                  const clientType = client.client_type || '-';
                  
                  // 移动应用
                  const mobileApp = client.mobile_app && client.mobile_app !== 'Unknown' ? client.mobile_app : '';
                  
                  // 语言信息（提取首选语言）
                  const language = client.accept_language 
                    ? client.accept_language.split(',')[0].trim().split(';')[0] 
                    : '-';

                  return (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2,
                      padding: '2px 0'
                    }}>
                      {renderInfoRow('设备类型', deviceTypeTag)}
                      {renderInfoRow('品牌型号', brandModel)}
                      {renderInfoRow('操作系统', osInfo)}
                      {renderInfoRow('浏览器', browserInfo)}
                      {renderInfoRow('客户端', clientType)}
                      {mobileApp && renderInfoRow('移动应用', <Tag color="purple" style={{ fontSize: '11px', padding: '0 4px' }}>{mobileApp}</Tag>)}
                      {renderInfoRow('语言', language)}
                    </div>
                  );
                },
              },
              {
                title: 'IP检测',
                dataIndex: 'ip_details',
                key: 'ip_detection',
                width: 150,
                render: (ipDetails) => {
                  if (!ipDetails) return '-';
                  
                  const tags = [];
                  
                  // 代理检测
                  if (ipDetails.is_proxy) {
                    tags.push(<Tag key="proxy" color="orange">代理</Tag>);
                  }
                  
                  // VPN检测
                  if (ipDetails.is_vpn) {
                    tags.push(<Tag key="vpn" color="orange">VPN</Tag>);
                  }
                  
                  // 数据中心
                  if (ipDetails.is_datacenter) {
                    tags.push(<Tag key="datacenter" color="blue">数据中心</Tag>);
                  }
                  
                  // Tor网络
                  if (ipDetails.is_tor) {
                    tags.push(<Tag key="tor" color="red">Tor</Tag>);
                  }
                  
                  // 卫星网络
                  if (ipDetails.is_satellite) {
                    tags.push(<Tag key="satellite" color="purple">卫星</Tag>);
                  }
                  
                  // 中继网络
                  if (ipDetails.is_relay) {
                    tags.push(<Tag key="relay" color="cyan">中继</Tag>);
                  }
                  
                  // 风险等级
                  if (ipDetails.risk_level) {
                    const riskColor = ipDetails.risk_level === 'low' ? 'green' : 
                                     ipDetails.risk_level === 'medium' ? 'orange' : 'red';
                    const riskText = ipDetails.risk_level === 'low' ? '低风险' : 
                                    ipDetails.risk_level === 'medium' ? '中风险' : '高风险';
                    tags.push(<Tag key="risk" color={riskColor}>{riskText}</Tag>);
                  }
                  
                  return tags.length > 0 ? (
                    <Space wrap size={[4, 4]}>
                      {tags}
                    </Space>
                  ) : (
                    <Tag color="green">正常</Tag>
                  );
                },
              },
              {
                title: '数据来源',
                key: 'data_source',
                width: 120,
                render: (_: unknown, record: FormattedVisitorRecord) => {
                  const tags = [];
                  if (record.use_ipcore === 1) {
                    tags.push(<Tag key="ipcore" color="blue">IP 库</Tag>);
                  }
                  if (record.use_fingerprint === 1) {
                    tags.push(<Tag key="fingerprint" color="cyan">指纹</Tag>);
                  }
                  return tags.length > 0 ? (
                    <Space wrap size={[4, 4]}>
                      {tags}
                    </Space>
                  ) : (
                    <Tag color="default">AI</Tag>
                  );
                },
              },
              {
                title: 'User-Agent',
                dataIndex: 'user_agent',
                key: 'user_agent',
                width: 280,
                render: (ua: string) => {
                  if (!ua) return '-';
                  return (
                    <Tooltip title={ua} placement="topLeft">
                      <div
                        style={{
                          maxWidth: '260px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '12px',
                          color: '#595959',
                          fontFamily: 'monospace',
                          cursor: 'pointer'
                        }}
                      >
                        {ua}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '指纹ID',
                dataIndex: 'fingerprint_id',
                key: 'fingerprint_id',
                width: 150,
                render: (fpId: string) => {
                  if (!fpId) return <span style={{ color: '#d9d9d9' }}>-</span>;
                  return (
                    <Tooltip title={fpId} placement="topLeft">
                      <Tag color="purple" style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                        {fpId.length > 16 ? `${fpId.substring(0, 16)}...` : fpId}
                      </Tag>
                    </Tooltip>
                  );
                },
              },
              {
                title: '操作',
                key: 'action',
                width: 220,
                fixed: 'right',
                render: (_, record) => {
                  const fbclid = getRecordFbclid(record);
                  return (
                    <Space size="small">
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={() => {
                          setSelectedRecord(record);
                          setDetailVisible(true);
                        }}
                      >
                        详情
                      </Button>
                      <Tooltip title="手动像素回传">
                        <Button
                          type="link"
                          icon={<SendOutlined style={{ color: '#1890ff' }} />}
                          size="small"
                          onClick={() => {
                            setPostbackVisitorId(record.raw?.id || 0);
                            setPostbackModalVisible(true);
                          }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          回传
                        </Button>
                      </Tooltip>
                      <Tooltip title="加入黑名单">
                        <Button
                          type="link"
                          danger
                          icon={<StopOutlined />}
                          size="small"
                          onClick={() => handleOpenBlacklistModal(record)}
                        >
                          拉黑
                        </Button>
                      </Tooltip>
                    </Space>
                  );
                },
              },
            ]}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
          />
          </div>
        </Card>

      {/* 访客记录详情抽屉 */}
      <Drawer
        title="访客记录详情"
        placement="right"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedRecord && (
          <div>
            {(() => {
              const fbclid = getRecordFbclid(selectedRecord);
              return (
                <Card
                  size="small"
                  title={
                    <Space>
                      <FacebookFilled style={{ color: '#1877F2' }} />
                      <Text strong>Facebook 回传工具</Text>
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                >
                  {fbclid ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Alert
                        type="success"
                        showIcon
                        message="检测到 Facebook Click ID"
                        description={(
                          <div>
                            <Text type="secondary">点击下方按钮可以复制或回传事件。</Text>
                            <Paragraph
                              copyable={{ text: fbclid, tooltips: ['复制 fbclid', '复制成功'] }}
                              style={{ marginBottom: 0, fontFamily: 'monospace' }}
                            >
                              {fbclid}
                            </Paragraph>
                          </div>
                        )}
                      />
                      <Space wrap>
                        <Tooltip title="复制 fbclid">
                          <Button icon={<CopyOutlined />} onClick={() => handleCopyText(fbclid, 'FBCLID 已复制')}>
                            复制 fbclid
                          </Button>
                        </Tooltip>
                        <Tooltip title="生成 Conversion API 所需事件配置">
                          <Button type="primary" icon={<SendOutlined />} onClick={() => handleOpenFacebookModal(selectedRecord)}>
                            回传 Facebook 事件
                          </Button>
                        </Tooltip>
                      </Space>
                    </Space>
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="未检测到 Facebook Click ID"
                      description="本次访问缺少 fbclid 参数，无法执行回传操作。"
                    />
                  )}
                </Card>
              );
            })()}
            <Descriptions title="基本信息" bordered column={1}>
              <Descriptions.Item label="链接ID">
                {selectedRecord.link_id}
              </Descriptions.Item>
              <Descriptions.Item label="链接名称">
                {selectedRecord.link_title || selectedRecord.link_code}
              </Descriptions.Item>
              <Descriptions.Item label="客户ID">
                {selectedRecord.client_id}
              </Descriptions.Item>
              <Descriptions.Item label="规则ID">
                {(() => {
                  const ruleResult = selectedRecord.rule_match_result;
                  if (!ruleResult) return '-';
                  const match = ruleResult.match(/\d+/);
                  return match ? match[0] : ruleResult;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="广告投放链接">
                {selectedRecord.request_url}
              </Descriptions.Item>
              <Descriptions.Item label="访问链接">
                {selectedRecord.access_url}
              </Descriptions.Item>
              <Descriptions.Item label="来源页面">
                {selectedRecord.referer || '直接访问'}
              </Descriptions.Item>
              <Descriptions.Item label="访问时间">
                {selectedRecord.ip_info.accessed_at}
              </Descriptions.Item>
              <Descriptions.Item label="访问状态">
                <Tag color={selectedRecord.access_status.is_intercepted ? 'red' : 'green'}>
                  {selectedRecord.access_status.is_intercepted ? '拦截' : '正常'}
                </Tag>
                {selectedRecord.access_status.is_intercepted && selectedRecord.access_status.intercept_reason}
              </Descriptions.Item>
              <Descriptions.Item label="爬虫检测">
                {renderBotTag(selectedRecord.bot_info)}
              </Descriptions.Item>
              <Descriptions.Item label="跳转结果">
                <Tag color={selectedRecord.redirect_type === 'default' ? 'green' : 'red'}>
                  {selectedRecord.redirect_type === 'default' ? '默认地址' : '拦截地址'}
                </Tag>
                <div style={{ marginTop: 8, wordBreak: 'break-all' }}>
                  {selectedRecord.redirect_url}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="响应状态码">
                {rawSelectedRecord?.response_code ?? '未提供'}
              </Descriptions.Item>
              {/* <Descriptions.Item label="处理耗时">
                {formatProcessingTime(rawSelectedRecord?.processing_time)}
              </Descriptions.Item>
              <Descriptions.Item label="数据分区">
                {rawSelectedRecord?.ds ?? '未提供'}
              </Descriptions.Item> */}
            </Descriptions>

            <Divider />

            <Descriptions title="IP和网络信息" bordered column={1}>
              <Descriptions.Item label="IP地址">
                {selectedRecord.ip_info.ip}
              </Descriptions.Item>
              <Descriptions.Item label="地理位置">
                {selectedRecord.ip_info.country_code} {selectedRecord.ip_info.region} {selectedRecord.ip_info.city}
              </Descriptions.Item>
              <Descriptions.Item label="地理坐标">
                {formatCoordinate(selectedRecord.ip_info.latitude, selectedRecord.ip_info.longitude)}
              </Descriptions.Item>
              <Descriptions.Item label="邮政编码">
                {selectedRecord.ip_info.postal_code || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="时区">
                {selectedRecord.ip_info.timezone || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="ASN">
                {selectedRecord.ip_details.asn} - {selectedRecord.ip_details.asn_name}
              </Descriptions.Item>
              <Descriptions.Item label="ASN类型">
                {selectedRecord.ip_details.asn_type || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="ASN路由">
                {selectedRecord.ip_details.asn_route || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="ASN组织">
                {selectedRecord.ip_details.asn_org || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="网络类型">
                {selectedRecord.ip_details.network_type || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="域名">
                {selectedRecord.ip_details.ip_domain || '无域名'}
              </Descriptions.Item>
              <Descriptions.Item label="主机名">
                {selectedRecord.ip_details.ip_hostname || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="托管域名数量">
                {selectedRecord.ip_details.hosted_domains_count || 0}
              </Descriptions.Item>
              <Descriptions.Item label="网络特征">
                <Space wrap>
                  {getNetworkFeatureTags(selectedRecord.ip_details).map((tag, index) => (
                    <Tag key={index} color={tag.color}>
                      {tag.text}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="代理检测">
                {renderSecurityTag(selectedRecord.ip_details.is_proxy, '识别为代理', '未识别代理')}
              </Descriptions.Item>
              <Descriptions.Item label="VPN检测">
                {renderSecurityTag(selectedRecord.ip_details.is_vpn, '识别为VPN', '未识别VPN')}
              </Descriptions.Item>
              <Descriptions.Item label="数据中心识别">
                {renderSecurityTag(selectedRecord.ip_details.is_datacenter, '识别为数据中心', '未识别数据中心')}
              </Descriptions.Item>
              <Descriptions.Item label="卫星网络">
                {renderSecurityTag(selectedRecord.ip_details.is_satellite, '识别为卫星网络', '未识别卫星网络')}
              </Descriptions.Item>
              <Descriptions.Item label="Tor网络">
                {renderSecurityTag(selectedRecord.ip_details.is_tor, '识别为Tor网络', '未识别Tor网络')}
              </Descriptions.Item>
              <Descriptions.Item label="中继网络">
                {renderSecurityTag(selectedRecord.ip_details.is_relay, '识别为中继网络', '未识别中继网络')}
              </Descriptions.Item>
              {selectedRecord.ip_details.proxy_type && (
                <Descriptions.Item label="代理类型">
                  {selectedRecord.ip_details.proxy_type}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="IP信任等级">
                <div>
                  <Tag color={
                    selectedRecord.ip_details.risk_level === 'low' ? 'green' : 
                    selectedRecord.ip_details.risk_level === 'medium' ? 'orange' : 'red'
                  }>
                    {selectedRecord.ip_details.risk_level === 'low' ? '优良等级' : 
                     selectedRecord.ip_details.risk_level === 'medium' ? '一般等级' : '风险等级'}
                  </Tag>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                    原始值: {selectedRecord.ip_details.risk_level || '未知'}
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Descriptions title="访客信息" bordered column={1}>
              <Descriptions.Item label="设备信息">
                <div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>品牌型号:</strong> {selectedRecord.device_system.device_brand || '未知'} {selectedRecord.device_system.device_model || ''}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>设备类型:</strong> 
                    <Tag color={
                      selectedRecord.device_system.device_type === 'mobile' ? 'blue' :
                      selectedRecord.device_system.device_type === 'tablet' ? 'green' :
                      selectedRecord.device_system.device_type === 'desktop' ? 'orange' : 'default'
                    }>
                      {selectedRecord.device_system.device_type === 'mobile' ? '移动设备' :
                       selectedRecord.device_system.device_type === 'tablet' ? '平板设备' :
                       selectedRecord.device_system.device_type === 'desktop' ? '桌面设备' : '其他'}
                    </Tag>
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="操作系统">
                {selectedRecord.device_system.os_type} {selectedRecord.device_system.os_version}
              </Descriptions.Item>
              <Descriptions.Item label="浏览器">
                {selectedRecord.client_info.browser_type} {selectedRecord.client_info.browser_version}
              </Descriptions.Item>
              <Descriptions.Item label="客户端类型">
                {selectedRecord.client_info.client_type || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="移动应用">
                {selectedRecord.client_info.mobile_app || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="语言">
                <div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>接受语言:</strong> {selectedRecord.client_info.accept_language || '未知'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedRecord.client_info.accept_language ? 
                      `支持的语言: ${selectedRecord.client_info.accept_language
                        .split(',')
                        .map((lang: string) => lang.trim().split(';')[0])
                        .join(', ')}` : 
                      '无语言信息'
                    }
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="来源页面">
                {selectedRecord.access_source}
              </Descriptions.Item>
              <Descriptions.Item label="User-Agent">
                <div style={{ wordBreak: 'break-all', fontSize: '12px' }}>
                  {selectedRecord.user_agent || '未知'}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      <Modal
        title="回传 Facebook 事件"
        open={fbEventModalVisible}
        onCancel={() => setFbEventModalVisible(false)}
        onOk={() => fbEventForm.submit()}
        confirmLoading={fbSubmitLoading}
        okText="生成事件"
        cancelText="取消"
        destroyOnClose
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="回传说明"
            description="系统暂未直接调用 Facebook Conversion API，此处先生成所需事件参数，方便后续集成。"
          />

          <Form
            layout="vertical"
            form={fbEventForm}
            onFinish={handleSubmitFacebookEvent}
            preserve={false}
          >
            <Form.Item label="Facebook Click ID">
              <Paragraph
                copyable={{ text: getRecordFbclid(selectedRecord) || '', tooltips: ['复制 fbclid', '复制成功'] }}
                style={{ marginBottom: 0, fontFamily: 'monospace' }}
              >
                {getRecordFbclid(selectedRecord) || '暂无数据'}
              </Paragraph>
            </Form.Item>
            <Form.Item
              name="event_name"
              label="事件名称"
              rules={[{ required: true, message: '请选择事件名称' }]}
            >
              <Select
                placeholder="选择 Facebook 标准事件"
                options={facebookEventOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="action_source"
              label="事件来源"
              rules={[{ required: true, message: '请选择事件来源' }]}
            >
              <Select
                options={[
                  { label: '聊天 (chat)', value: 'chat' },
                  { label: '电话 (phone_call)', value: 'phone_call' },
                  { label: '网站 (website)', value: 'website' },
                  { label: '其他 (other)', value: 'other' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="event_time"
              label="事件时间"
              rules={[{ required: true, message: '请选择事件时间' }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="custom_data_value" label="自定义金额 (可选)">
              <Input placeholder="例如：199.00" />
            </Form.Item>
            <Form.Item name="currency" label="货币代码" initialValue="USD">
              <Input placeholder="例如：USD" />
            </Form.Item>
            <Form.Item name="notes" label="备注 (可选)">
              <Input.TextArea rows={3} placeholder="记录聊单结果或其他信息" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 像素回传弹窗 */}
      <PostbackModal
        visible={postbackModalVisible}
        visitorId={postbackVisitorId}
        onClose={() => setPostbackModalVisible(false)}
      />

      {/* 黑名单弹窗 */}
      <Modal
        title="加入黑名单"
        open={blacklistModalVisible}
        onCancel={() => { setBlacklistModalVisible(false); blacklistForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={blacklistForm} layout="vertical" onFinish={handleSubmitBlacklist}>
          <Form.Item
            name="blacklist_type"
            label="黑名单类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select 
              placeholder="请选择类型"
              onChange={(value: BlacklistType) => {
                if (blacklistRecord) {
                  blacklistForm.setFieldsValue({
                    identifier: value === 'ip' ? blacklistRecord.ip_info.ip : '',
                  });
                }
              }}
            >
              <Select.Option value="ip">IP 地址</Select.Option>
              <Select.Option value="fingerprint">指纹 ID</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="identifier"
            label="标识"
            rules={[{ required: true, message: '请输入标识' }]}
            extra="IP类型输入IP地址，指纹类型输入 Fingerprint visitor_id"
          >
            <Input placeholder="请输入IP地址或指纹ID" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="请输入备注（可选）" rows={3} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setBlacklistModalVisible(false); blacklistForm.resetFields(); }}>
                取消
              </Button>
              <Button type="primary" danger htmlType="submit">
                加入黑名单
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VisitorManagement; 